import Layout from "./home/Layout";
import { Navigate, Route, Routes } from "react-router-dom";
import Home from "./home";
import { Unauthorized } from "./error-components/Unauthorized";
import { Unavailable } from "./error-components/Unavailable";
import Users from "./users/Users";
import CheckTypesPage from "./check-types/CheckTypes";
import Companies from "./companies/Companies";
import Workflows from "./workflows/Workflows";
import ReportsCompleted from "./reports/ReportsCompleted";
import ReportsInProcess from "./reports/ReportsInProcess";
import InternalQATab from "./reports/InternalQATab";
import ClientQATab from "./reports/ClientQATab";
import ReportsPendingReview from "./reports/ReportsPendingReview";
import ReportsReviewed from "./reports/ReportsReviewed";
import BlueprintForm from "./blueprints/BlueprintForm";
import Reports from "./reports/Reports";
import ReportDetail from "./reports/ReportDetail";
import ReportLogs from "./reports/ReportLogs";
import WorkflowDebug from "./reports/WorkflowDebug";
import ReportsAmendment from "./reports/ReportsAmendment";
import PersonFormV2 from "./persons/form-multi/PersonFormv2";
import PersonForm from "./persons/PersonForm";
import WorkflowStatus from "./workflows/WorkFlowStatus";
import { Logout } from "./home/Logout";
import BehaviorKitForm from "./companies/BehaviorKit/BehaviorKitForm";
import ReportsInProgress from "./reports/ReportsInProgress";
import {
  ClientQATabV2026,
  InternalQATabV2026,
  ReportsInProcessV2026,
  ReportsInProgressV2026,
  ReportsPendingReviewV2026,
  ReportsReviewedV2026,
} from "./reports/list-views-v2026/pages";
import ReportsBulkUpload from "./persons/bulk-upload/ReportsBulkUpload";
import { useEffect, useState } from "react";
import axios from "axios";
import { useAppContext } from "./AppProviders";
import { useAuth0 } from "@auth0/auth0-react";
import CheckAssignment from "./workflows/assignment/CheckAssignment";
import ReportsNextTask from "./reports/ReportsNextTask";
import LandingPage from "./landing-page";
import { processNetworkLog } from "./utils";
import ControlGroupWrapper from "./shared-components/ControlGroupWrapper";
import Consent from "./consent/Consent";
import ReportsTasksQueue from "./reports/ReportsTasksQueue";
import ThreeSixty from "./360/ThreeSixty";
import ThreeSixtyCompare from "./360/ThreeSixtyCompare";
import ThreeSixtyQueueing from "./360/ThreeSixtyQueuing";
import LegacyReportSummary from "./legacy/LegacyReportSummary";
import OperationView from "./admin/operation-view/OperationView";
import Admin from "./admin/Admin";
import AdminCompleted from "./admin/admin-completed/Completed";
import ReportsLegacyCoreReport from "./reports/ReportsLegacyCoreReport";
import Reports360Legacy from "./reports/Reports360Legacy";
import Workbench360 from "./360/Workbench360";
import Review360 from "./360/360Review";
import { WebResultsView } from "./360/WebResultsView";
import ReportResolver from "./reports/ReportResolver";
import { CompanySubscriptions } from "./companies/Subscriptions";
import ReportsWizard from "./reports-wizard/ReportsWizard";
import MisconductCompare from "./reports/MisconductCompare";
import CompanyIntegrations from "./companies/vertical/integrations/CompanyIntegrations";
import {
  SettingsLayout,
  Subscriptions,
  SubscriptionDetails,
  ApiSettings,
  ReportSettings,
  GeneralSettings,
  ReportTypes,
  BehaviorKits,
  Integrations,
} from "./companies/vertical";
import SubCompanyListPage from "./companies/vertical/SubCompanyListPage";
import CheckTypes from "./companies/vertical/checktypes/CheckTypes";
import NewReport from "./reports/NewReport";
import SelfServe from "./self-serve/SelfServe";
import LegacyUserList from "./legacy/users/LegacyUserList";
import LegacyPasswordReset from "./legacy/users/LegacyPasswordReset";
import ThreeSixtyInvestigating from "./360/ThreeSixtyInvestigating";
import ThreeSixtyQA from "./360/ThreeSixtyQA";
import Summary360 from "./360/summary/Summary360";
import ThreeSixtyFinished from "./360/ThreeSixtyFinished";
import ThreeSixtyWebSearch from "./360/ThreeSixtyWebSearch";
import ThreeSixtyAmendment from "./360/ThreeSixtyAmendment";
import ThreeSixtyQueuingV2026 from "./360/list-views-v2026/ThreeSixtyQueuing";
import ThreeSixtyWebSearchV2026 from "./360/list-views-v2026/ThreeSixtyWebSearch";
import ThreeSixtyInvestigatingV2026 from "./360/list-views-v2026/ThreeSixtyInvestigating";
import ThreeSixtyQAV2026 from "./360/list-views-v2026/ThreeSixtyQA";
import ThreeSixtyFinishedV2026 from "./360/list-views-v2026/ThreeSixtyFinished";
import ThreeSixtyAmendmentV2026 from "./360/list-views-v2026/ThreeSixtyAmendment";
import { ThreeSixtyListViewByFlag } from "./360/ThreeSixtyListViewByFlag";
import {
  MisconductListViewByFlag,
  misconductListRouteElements,
} from "./reports/MisconductListViewByFlag";
import UsageReport from "./usage-report/UsageReport";
import CustomBehaviorPrompts from "./custom-behavior-prompts/CustomBehaviorPrompts";

export function AnonymousApp() {
  return (
    <Routes>
      <Route
        path="legacy/password-reset/:token"
        element={<LegacyPasswordReset />}
      />
    </Routes>
  );
}

function App() {
  const { getAccessTokenSilently } = useAuth0();
  const { resetAccessToken, logMessage } = useAppContext();
  const [requestRetries, setRequestRetries] = useState(0);
  useEffect(() => {
    // Check expiry date before sending request. Refresh token if expired
    axios.interceptors.request.use(
      async function (config) {
        try {
          const token = await getAccessTokenSilently();
          // @ts-ignore
          config.headers["Authorization"] = `${token}`;
        } catch (e) {
          console.error(e);
        }
        return config;
      },
      function (error) {
        error = processNetworkLog(error, logMessage);
        return Promise.reject(error);
      }
    );

    // If 401 due to token expiry then refresh (in edge case where on the frontend expiration is good, but with the time
    // it takes to make the request it expires in the backend
    axios.interceptors.response.use(
      (response) => {
        setRequestRetries(0);
        return response;
      },
      function (error) {
        const status = error.response ? error.response.status : null;
        if (
          error?.response?.data?.message === "token is expired" &&
          requestRetries === 0
        ) {
          return resetAccessToken().then((accessToken) => {
            error.config.headers["Authorization"] = `${accessToken}`;
            setRequestRetries(requestRetries + 1);
            return axios.request(error.config);
          });
        } else if (status === 401) {
          window.location.href = `${window.location.origin}/#/logout`;
          setRequestRetries(0);
          error = processNetworkLog(error, logMessage);
          return Promise.reject(error);
        } else if ([301, 302].includes(error.response?.status)) {
          const redirectUrl = error.response.headers.location;
          console.error("Redirecting to", redirectUrl);
          return axios.get(redirectUrl);
        }
        error = processNetworkLog(error, logMessage);
        return Promise.reject(error);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Routes>
      <Route index element={<LandingPage />} />
      <Route path="landing" element={<LandingPage />} />
      <Route path="/*" element={<Layout />}>
        <Route path="unauthorized" element={<Unauthorized />} />
        <Route path="unavailable" element={<Unavailable />} />
        <Route path="logout" element={<Logout />} />
        <Route path="home" element={<Home />} />
        <Route path="users" element={<Users />} />
        <Route path="check-types" element={<CheckTypesPage />} />
        <Route path="check-types/new" element={<BlueprintForm />} />
        <Route path="blueprints" element={<Navigate to="/check-types" replace />} />
        <Route
          path="blueprints/new"
          element={<Navigate to="/check-types/new" replace />}
        />
        <Route path="companies" element={<Companies />} />
        <Route path="newreport" element={<NewReport />} />
        <Route
          path="companies/:companyId/behaviorkit/new"
          element={<BehaviorKitForm />}
        />
        <Route
          path="companies/:companyId/behaviorkit/:behaviorKitId/edit"
          element={<BehaviorKitForm />}
        />
        <Route
          path="companies/:companyId/subscriptions"
          element={<CompanySubscriptions />}
        />
        <Route
          path="companies/:companyId/integrations"
          element={<CompanyIntegrations />}
        />
        <Route
          path="companies/:companyId/subcompany/:subid/subscriptions"
          element={<CompanySubscriptions />}
        />
        <Route
          path="companies/:companyId/subcompany/:subid/integrations"
          element={<CompanyIntegrations />}
        />
        <Route path="companies/:companyId" element={<SettingsLayout />}>
          <Route path="settings" element={<Navigate to="behavior-kits" />} />
          <Route path="settings/general" element={<GeneralSettings />} />
          <Route path="settings/api" element={<ApiSettings />} />
          <Route path="settings/reports" element={<ReportSettings />} />
          <Route path="settings/report-types" element={<ReportTypes />} />
          <Route path="settings/checktypes" element={<CheckTypes />} />
          <Route path="settings/behavior-kits" element={<BehaviorKits />} />
          <Route path="settings/subscriptions" element={<Subscriptions />} />
          <Route
            path="settings/subscriptions/:recurUuid"
            element={<SubscriptionDetails />}
          />
          <Route path="settings/integrations" element={<Integrations />} />
          <Route path="subcompanies" element={<SubCompanyListPage />} />
        </Route>
        <Route
          path="companies/:companyId/subcompany/:subid"
          element={<SettingsLayout />}
        >
          <Route path="settings" element={<Navigate to="behavior-kits" />} />
          <Route path="settings/general" element={<GeneralSettings />} />
          <Route path="settings/api" element={<ApiSettings />} />
          <Route path="settings/reports" element={<ReportSettings />} />
          <Route path="settings/report-types" element={<ReportTypes />} />
          <Route path="settings/behavior-kits" element={<BehaviorKits />} />
          <Route path="settings/subscriptions" element={<Subscriptions />} />
          <Route
            path="settings/subscriptions/:recurUuid"
            element={<SubscriptionDetails />}
          />
          <Route path="settings/checktypes" element={<CheckTypes />} />
          <Route path="settings/integrations" element={<Integrations />} />
        </Route>
        <Route path="workflows" element={<Workflows />} />
        <Route
          path="workflows/status/:workflowStepStatus"
          element={<WorkflowStatus />}
        />
        <Route path="workflows/checkassignment" element={<CheckAssignment />} />
        <Route
          path="reports/misconduct-compare"
          element={<MisconductCompare />}
        />
        <Route path="reports/*" element={<Reports />}>
          <Route
            index
            element={
              <MisconductListViewByFlag
                v2025={<ReportsInProcess />}
                v2026={<ReportsInProcessV2026 />}
              />
            }
          />
          {misconductListRouteElements({
            path: "in_process",
            v2025: <ReportsInProcess />,
            v2026: <ReportsInProcessV2026 />,
          })}
          {misconductListRouteElements({
            path: "in_progress",
            v2025: <ReportsInProgress />,
            v2026: <ReportsInProgressV2026 />,
          })}
          {misconductListRouteElements({
            path: "internal_qa",
            v2025: <InternalQATab />,
            v2026: <InternalQATabV2026 />,
          })}
          {misconductListRouteElements({
            path: "client_qa",
            v2025: <ClientQATab />,
            v2026: <ClientQATabV2026 />,
          })}
          <Route path="completed" element={<ReportsCompleted />} />
          {misconductListRouteElements({
            path: "pending_review",
            v2025: <ReportsPendingReview />,
            v2026: <ReportsPendingReviewV2026 />,
          })}
          {misconductListRouteElements({
            path: "reviewed",
            v2025: <ReportsReviewed />,
            v2026: <ReportsReviewedV2026 />,
          })}
          <Route path="amendment" element={<ReportsAmendment />} />
          <Route path="legacy" element={<ReportsLegacyCoreReport />} />
          <Route path="360_legacy" element={<Reports360Legacy />} />
        </Route>
        <Route path="360/compare" element={<ThreeSixtyCompare />} />
        <Route path="360/*" element={<ThreeSixty />}>
          <Route
            path="queuing"
            element={
              <ThreeSixtyListViewByFlag
                v2025={<ThreeSixtyQueueing />}
                v2026={<ThreeSixtyQueuingV2026 />}
              />
            }
          />
          <Route path="queuing/v2025" element={<ThreeSixtyQueueing />} />
          <Route path="queuing/v2026" element={<ThreeSixtyQueuingV2026 />} />
          <Route
            path="web_search"
            element={
              <ThreeSixtyListViewByFlag
                v2025={<ThreeSixtyWebSearch />}
                v2026={<ThreeSixtyWebSearchV2026 />}
              />
            }
          />
          <Route path="web_search/v2025" element={<ThreeSixtyWebSearch />} />
          <Route
            path="web_search/v2026"
            element={<ThreeSixtyWebSearchV2026 />}
          />
          <Route
            path="investigating"
            element={
              <ThreeSixtyListViewByFlag
                v2025={<ThreeSixtyInvestigating />}
                v2026={<ThreeSixtyInvestigatingV2026 />}
              />
            }
          />
          <Route
            path="investigating/v2025"
            element={<ThreeSixtyInvestigating />}
          />
          <Route
            path="investigating/v2026"
            element={<ThreeSixtyInvestigatingV2026 />}
          />
          <Route
            path="qa"
            element={
              <ThreeSixtyListViewByFlag
                v2025={<ThreeSixtyQA />}
                v2026={<ThreeSixtyQAV2026 />}
              />
            }
          />
          <Route path="qa/v2025" element={<ThreeSixtyQA />} />
          <Route path="qa/v2026" element={<ThreeSixtyQAV2026 />} />
          <Route
            path="finished"
            element={
              <ThreeSixtyListViewByFlag
                v2025={<ThreeSixtyFinished />}
                v2026={<ThreeSixtyFinishedV2026 />}
              />
            }
          />
          <Route path="finished/v2025" element={<ThreeSixtyFinished />} />
          <Route path="finished/v2026" element={<ThreeSixtyFinishedV2026 />} />
          <Route
            path="amendment"
            element={
              <ThreeSixtyListViewByFlag
                v2025={<ThreeSixtyAmendment />}
                v2026={<ThreeSixtyAmendmentV2026 />}
              />
            }
          />
          <Route path="amendment/v2025" element={<ThreeSixtyAmendment />} />
          <Route
            path="amendment/v2026"
            element={<ThreeSixtyAmendmentV2026 />}
          />
        </Route>
        <Route path="360/:id/workbench" element={<Workbench360 />} />
        <Route path="360/:id/review" element={<Review360 />} />
        <Route path="360/:id/summary" element={<Summary360 />} />
        <Route path="360/:id/web-results" element={<WebResultsView />} />
        <Route path="360/:id/qa-workbench" element={<Workbench360 />} />
        <Route path="360/:id/qa-review" element={<Review360 />} />
        <Route path="360/:id/misconduct-qa" element={<ReportDetail />} />
        <Route path="reports/:id/amendment/detail" element={<ReportDetail />} />
        <Route
          path="reports/:id/amendment/post-edit"
          element={<ReportDetail />}
        />
        <Route
          path="reports/:id/amendment/post-rate"
          element={<ReportDetail />}
        />
        <Route path="reports/:id/amendment/system" element={<ReportDetail />} />
        <Route path="reports/:id/amendment/scrape" element={<ReportDetail />} />
        <Route
          path="reports/:id/amendment/article-edit"
          element={<ReportDetail />}
        />
        <Route path="reports/:id/webcontent" element={<ReportDetail />} />
        <Route path="reports/:id/detail" element={<ReportDetail />} />
        <Route path="reports/:id/post-rate" element={<ReportDetail />} />
        <Route
          path="reports/legacy/:id/summary"
          element={<LegacyReportSummary />}
        />
        <Route path="person/:id/report" element={<ReportResolver />} />
        <Route path="reports-wizard" element={<ReportsWizard />} />

        <Route path="reports/:id/summary" element={<ReportDetail />} />
        <Route path="reports/:id/system" element={<ReportDetail />} />
        <Route path="reports/:id/scrape" element={<ReportDetail />} />
        <Route path="reports/:id/iqa" element={<ReportDetail />} />
        <Route path="reports/:id/cqa" element={<ReportDetail />} />
        <Route path="reports/:id/aqa" element={<ReportDetail />} />
        <Route path="reports/bulkupload" element={<ReportsBulkUpload />} />
        <Route path="reports/next_task" element={<ReportsNextTask />} />

        <Route path="reports/enter_queue" element={<ReportsTasksQueue />} />
        <Route
          path="reports/no_queue"
          element={<ReportsTasksQueue noQueue={true} />}
        />

        <Route path="reports/:id/logs" element={<ReportLogs />} />
        <Route path="reports/:id/debug" element={<WorkflowDebug />} />
        <Route path="person/new" element={<PersonForm />} />
        <Route path="person/newmulti" element={<PersonFormV2 />} />
        <Route path="admin/*" element={<Admin />}>
          <Route path="completed" element={<AdminCompleted />} />
          <Route path="operation" element={<OperationView />} />
          <Route path="legacy/users" element={<LegacyUserList />} />
        </Route>
        <Route path="*" element={<Unauthorized />} />
        <Route
          path="consent-forms"
          element={
            <ControlGroupWrapper>
              <Consent />
            </ControlGroupWrapper>
          }
        />
        <Route
          path="self-serve"
          element={
            <ControlGroupWrapper>
              <SelfServe />
            </ControlGroupWrapper>
          }
        />
        <Route
          path="usage-report"
          element={
            <ControlGroupWrapper>
              <UsageReport />
            </ControlGroupWrapper>
          }
        />
        <Route
          path="custom-behavior-prompts"
          element={<CustomBehaviorPrompts />}
        />
      </Route>
    </Routes>
  );
}

export default App;
